/**
 * The project Home, the workspace's Dashboard tab (design "A calmer project Home"). It answers three questions, in
 * this order, and folds the rest away:
 *
 * 1. Is it working? The strip: name and description, who is here, one status chip, Open app · Start/Stop all ·
 *    Continue in chat, and "⋯" (rename, description, delete project). On desktop the header's tab row shows the Home's
 *    tools (`toolsSlot`, design "Steady tabs"): Start/Stop all and Open app move there, the run state is in the
 *    header's project row, and the strip keeps the rest.
 * 2. What changed? One list, five rows at most (home/WhatChanged.jsx); nothing new: one line.
 * 3. Where are things? Apps (one row per service) and Files ("Inputs (n) · Results (n)"); the whole page takes a drop,
 *    saved in `brain/inputs/original/` as a chat attachment is.
 *
 * When the project was sent to Mia-Platform Console, a card under the strip says where that delivery stands
 * (ConsoleDeliveryStatus.jsx: review, images, dev, the version per environment, Open in Console).
 *
 * Then "More about this project", closed: How it fits together, Brain, AI used, Conversations. A brand-new project
 * (nothing to run, no files) shows "Start by…" (describe it in chat, or import a folder or a zip) instead. A section
 * with nothing to show disappears.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { ArrowSquareOut } from '@phosphor-icons/react';
import ConfirmDialog from './ConfirmDialog';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import { listAdkSessions, parseVfsSessionId } from '../services/AdkSessionsService';
import * as vfsClient from '../utils/vfsClient';
import { buildMap, primaryService, servicePath, uniqueName } from '../utils/projectOverview';
import { isBrandNew } from '../utils/homeModel';
import { collectFromDataTransfer, isOsFileDrag } from '../utils/treeUpload/collectEntries';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import useProjectServices from '../hooks/useProjectServices';
import useServiceFolders, { notifyServicesChanged, refreshServiceFolders } from '../hooks/useServiceFolders';
import { servicesWithFolders } from '../utils/projectLayout';
import useDefaultApp from '../hooks/useDefaultApp';
import useProjectPresence from '../hooks/useProjectPresence';
import useChatFeature from '../hooks/useChatFeature';
import { usePlanTab } from '../hooks/usePlanFeatures';
import { useLiveRefresh } from '../utils/livePoll';
import ProjectRunStatus from './sandbox/ProjectRunStatus';
import RunAllActions from './sandbox/RunAllActions';
import { appItem, openApp, primaryItem, projectRunItems, startAndOpenApp } from './sandbox/projectRun';
import { isProjectSourceDrop } from './sandbox/ProjectImportOffer';
import { markHomeStep } from '../services/SettingsService';
import HomeStrip from './home/HomeStrip';
import ProjectWorkspaceChips from './workspaces/ProjectWorkspaceChips';
import WhatChanged from './home/WhatChanged';
import WhereThings from './home/WhereThings';
import FilesSection, { BrainGadget } from './home/FilesSection';
import MoreAboutProject from './home/MoreAboutProject';
import HomeGadgets from './home/HomeGadgets';
import { openHistory, openProjectFile } from '../utils/workspaceModel';
import NeedsAttention from './home/NeedsAttention';
import ReadyToPublish from './home/ReadyToPublish';
import RequestsGadget from './home/RequestsGadget';
import PlanUsage from './home/PlanUsage';
import PreviewGadget from './home/gadgets/PreviewGadget';
import CodeActivity from './home/gadgets/CodeActivity';
import DependenciesGadget from './home/gadgets/DependenciesGadget';
import AgentsAtWork from './home/gadgets/AgentsAtWork';
import OpenWork from './home/gadgets/OpenWork';
import FileGadget from './home/gadgets/FileGadget';
import TestsGadget from './home/gadgets/TestsGadget';
import ErrorsGadget, { openTraceInTab } from './home/gadgets/ErrorsGadget';
import ApiHealthGadget from './home/gadgets/ApiHealthGadget';
import SecurityGadget from './home/gadgets/SecurityGadget';
import ReleaseGadget from './home/gadgets/ReleaseGadget';
import EnvironmentsGadget from './home/gadgets/EnvironmentsGadget';
import ConfigHealthGadget from './home/gadgets/ConfigHealthGadget';
import UptimeGadget from './home/gadgets/UptimeGadget';
import JobsGadget from './home/gadgets/JobsGadget';
import DataGadget from './home/gadgets/DataGadget';
import CostGadget from './home/gadgets/CostGadget';
import BackupsGadget from './home/gadgets/BackupsGadget';
import PeopleGadget from './home/gadgets/PeopleGadget';
import ReviewsGadget from './home/gadgets/ReviewsGadget';
import { openMessages } from './collab/Messages';
import { refreshGadgetData } from '../utils/gadgetData';
import ConsoleDeliveryStatus from './ConsoleDeliveryStatus';
import StartBy from './home/StartBy';
import useProjectArchive from './projects/useProjectArchive';
import useProjectFavorite from './projects/useProjectFavorite';
import { useAddService } from './home/AddService';
import NewProjectDialog from './sandbox/NewProjectDialog';
import { shortServiceName } from '../utils/gitRepoSource';
import './DashboardView.css';

// Where a file dropped on the Home goes: the same place a chat attachment is stored.
const INPUTS_DIR = 'brain/inputs/original';
// How often the Home refreshes itself while it is shown (the services' state: every 5 s, useProjectServices).
export const HOME_REFRESH_MS = 30000;
const OUTPUTS_DIR = 'brain/outputs';

/** A tree node's visible children (no dot-files), as `{ name, type }`; `[]` for a file or a missing node. */
const visibleChildren = (node) => (Array.isArray(node?.children) ? node.children : [])
  .filter((child) => child?.name && !child.name.startsWith('.'))
  .map((child) => ({
    name: child.name,
    type: child.type === 'directory' ? 'directory' : 'file',
    ...(Number.isFinite(child.size) ? { size: child.size } : {}),
    ...(child.modified || child.mtime ? { modified: child.modified || child.mtime } : {}),
  }));
const childNamed = (node, name) => (Array.isArray(node?.children) ? node.children : []).find((child) => child?.name === name && child.type === 'directory') || null;

/** From `brain/`'s tree (depth 3): the files dropped in (INPUTS_DIR), the results (OUTPUTS_DIR), brain's own folders. */
export function brainListing(brain) {
  const [, inputsName, originalName] = INPUTS_DIR.split('/');
  const outputsName = OUTPUTS_DIR.split('/')[1];
  return {
    inputs: visibleChildren(childNamed(childNamed(brain, inputsName), originalName)),
    outputs: visibleChildren(childNamed(brain, outputsName)),
    // Each folder keeps its own files: an upload into it picks a free name against them, never over one.
    folders: visibleChildren(brain).map((folder) => (folder.type === 'directory' ? { ...folder, children: visibleChildren(childNamed(brain, folder.name)) } : folder)),
  };
}

/** The chat panel's composer, focused after "Continue in chat" (the panel itself is App's). */
const focusChat = () => setTimeout(() => document.querySelector('.chat-container textarea')?.focus(), 80);

const DashboardView = ({
  selectedProject,
  configuration,
  vfsMetadata,
  tenant,
  onResourceSelect,
  onProjectDeleted,
  onProjectUpdated,
  onVfsMetadataUpdated,
  onOpenFiles,
  onOpenCode = null,
  onOpenLogs = null,
  onOpenHandoff = null,
  onOpenSuggestion = null,
  onServicesChanged = null,
  onContinueInChat = null,
  toolsSlot = null,
  runToolsInHeader = false,
}) => {
  const { user } = useAuth();
  const userId = user?.sub || user?.id || user?.email;
  const projectId = selectedProject?.id;
  // Collaboration (presence, what changed) is for vfs-backed projects.
  const collab = Boolean(projectId && vfsClient.isVfsBackedProject(projectId));
  const sandbox = Boolean(config.sandboxFirst && projectId);
  // Sandbox-first (docs/SANDBOX_FIRST.md): each service's state comes from the pod the project runs in (its owner's,
  // per person), not from the caller's own sandbox overview. A viewer sees the state but gets no Start / Stop.
  const { services: sandboxServices, canRun, role, refresh: refreshSandbox } = useProjectServices(projectId, { enabled: sandbox, intervalMs: 5000 });
  // The project's service folders: the store the Apps tab and the header read (hooks/useServiceFolders), so Apps,
  // Needs attention and Start all list the services the Apps tab lists, a new one without a page reload.
  const { folders: serviceFolders } = useServiceFolders(sandbox ? projectId : null, { refreshKey: sandboxServices.length });
  const { defaultApp, defaultRunnable } = useDefaultApp(projectId);
  const presence = useProjectPresence(collab ? projectId : null);
  const [chatOn] = useChatFeature();
  const planShows = usePlanTab();
  const yourRole = role || presence?.you?.role || null;
  const canEdit = yourRole !== 'viewer';

  // ---- the project's files, conversations and AI use ----------------------------------------------------------------
  const [inputFiles, setInputFiles] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);
  const [brainFolders, setBrainFolders] = useState([]);
  const [brainLoaded, setBrainLoaded] = useState(false);
  // One look at `brain/` (three levels: brain → inputs → original → its files) instead of asking for
  // `brain/inputs/original` and `brain/outputs` on their own: either may not exist yet, and each missing folder was a
  // 404 in the console on every visit. The project root (always there) says first whether `brain/` exists at all.
  const refreshBrain = useCallback(async() => {
    if (!projectId) { return; }
    let listing = { inputs: [], outputs: [], folders: [] };
    try {
      const root = await vfsClient.readTree(projectId, '.', 1);
      const hasBrain = (root?.children || []).some((child) => child.name === 'brain' && child.type === 'directory');
      if (hasBrain) { listing = brainListing(await vfsClient.readTree(projectId, 'brain', 3)); }
    } catch (_error) {
      // vfs-service unreachable: the Home shows no files rather than an error.
    }
    setInputFiles(listing.inputs.filter((entry) => entry.type === 'file'));
    setOutputFiles(listing.outputs);
    setBrainFolders(listing.folders.filter((entry) => entry.type === 'directory'));
    setBrainLoaded(true);
  }, [projectId]);
  useEffect(() => { setBrainLoaded(false); refreshBrain(); }, [refreshBrain]);

  // The project on show now: an answer for another one (the person moved on while it was on its way) is dropped.
  const shownProjectRef = useRef(projectId);
  shownProjectRef.current = projectId;

  // Conversations: the ADK sessions of this project (its bound conversation, or an old `vfs-<project>` id).
  const [sessions, setSessions] = useState([]);
  const loadSessions = useCallback(async() => {
    if (!userId || !projectId) { setSessions([]); return; }
    try {
      const all = await listAdkSessions(userId);
      if (shownProjectRef.current !== projectId) { return; }
      setSessions((all || [])
        .filter((session) => session.id === vfsMetadata?.conversationId || parseVfsSessionId(session.id)?.projectId === projectId)
        .sort((a, b) => (b.lastUpdateTime || 0) - (a.lastUpdateTime || 0)));
    } catch {
      // Unreachable: the list stays as it was.
    }
  }, [userId, projectId, vfsMetadata?.conversationId]);
  useEffect(() => { setSessions([]); loadSessions(); }, [loadSessions]);

  // AI used: its summary says whether there is anything to show in More, and whether to raise Shadow AI.
  const [ai, setAi] = useState({ loaded: false, summary: null, shadow: null });
  const loadAi = useCallback(async() => {
    if (!projectId) { return; }
    try {
      const answer = await vfsClient.getProvenanceSummary(projectId);
      if (shownProjectRef.current === projectId) { setAi({ loaded: true, summary: answer?.summary || null, shadow: answer?.shadow || null }); }
    } catch {
      // Unreachable: what was shown stays.
    }
  }, [projectId]);
  useEffect(() => { setAi({ loaded: false, summary: null, shadow: null }); loadAi(); }, [loadAi]);

  // ---- live: the whole Home refreshes itself while it is shown (utils/livePoll.js), and on Refresh ------------------
  // The services' state has its own, faster poll (useProjectServices, every 5 s); this reloads it too, with the files,
  // the conversations, the AI summary, What changed (`refreshKey`) and More's restore points.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const reloadHome = useCallback(async() => {
    // The gadgets' shared answers (utils/gadgetData.js) of the ones on the board: read again too.
    await Promise.allSettled([refreshBrain(), loadSessions(), loadAi(), sandbox ? refreshSandbox() : null, sandbox ? refreshServiceFolders(projectId) : null, projectId ? refreshGadgetData(projectId) : null]);
    setRefreshKey((key) => key + 1);
  }, [refreshBrain, loadSessions, loadAi, sandbox, refreshSandbox, projectId]);
  const { refresh: refreshLive } = useLiveRefresh(reloadHome, { intervalMs: HOME_REFRESH_MS, enabled: Boolean(projectId) });
  const refreshNow = async() => {
    setRefreshing(true);
    try { await refreshLive(); } finally { setRefreshing(false); }
  };
  // Shown unless the summary says no AI was ever recorded (it shows its own error when it cannot load).
  const showAi = Boolean(projectId) && !(ai.loaded && !ai.summary?.events);

  // ---- name, description, delete ------------------------------------------------------------------------------------
  const displayName = vfsMetadata?.name || selectedProject?.name;
  const displayDescription = vfsMetadata?.description || selectedProject?.description;

  // A rename is written to both the Console record and `.mia/project.json` (which the Home reads first); either may
  // fail on its own. Resolves to an error to show, or null.
  const saveField = async(field, value) => {
    const tenantId = tenant?.tenantId || tenant?.id;
    const [consoleResult, vfsResult] = await Promise.allSettled([
      tenantId && projectId ? miaPlatformDataService.updateProject(tenantId, projectId, { [field]: value }) : Promise.reject(new Error('No tenant/project context')),
      projectId ? vfsClient.updateProjectMetadata(projectId, { [field]: value }) : Promise.reject(new Error('No project context')),
    ]);
    if (consoleResult.status === 'rejected' && vfsResult.status === 'rejected') { return `Failed to save the ${field}`; }
    if (vfsResult.status === 'fulfilled' && onVfsMetadataUpdated) { onVfsMetadataUpdated(vfsResult.value); }
    if (onProjectUpdated) { onProjectUpdated({ ...selectedProject, [field]: value }); }
    return null;
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  // Archive project… / Unarchive (the owner's) and the "Archived" banner: projects/useProjectArchive.
  const archive = useProjectArchive({ projectId, name: displayName, metadata: vfsMetadata, isOwner: !yourRole || yourRole === 'owner', enabled: sandbox, onChanged: (answer) => onVfsMetadataUpdated?.({ ...vfsMetadata, archived: answer.archived || undefined, archivedAt: answer.archivedAt || undefined }) });
  // Add to / Remove from favorites (the stars of the Projects page): projects/useProjectFavorite.
  const favorite = useProjectFavorite({ projectId, tenantId: tenant?.tenantId || tenant?.id || null });
  // Remix…: New project in remix mode on this project (sandbox-first).
  const [remixOpen, setRemixOpen] = useState(false);
  // Where the gadgets' Edit dashboard buttons sit: in the title row (HomeStrip's actions).
  const [barSlot, setBarSlot] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deleteProject = async() => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await miaPlatformDataService.deleteProject(projectId);
      onProjectDeleted?.(projectId);
    } catch (error) {
      message.error(error.message || 'Failed to delete the project');
    } finally {
      setDeleting(false);
    }
  };

  // ---- files in: the page is a drop zone ---------------------------------------------------------------------------
  const [upload, setUpload] = useState({ busy: false, error: null, added: [], paths: [] });
  const fileInputRef = useRef(null);
  // Into Inputs by default; a brain folder (`dir`, with the names already in it) from the Files card's Brain group.
  const uploadFiles = async(fileList, dir = INPUTS_DIR, existing = null) => {
    const files = Array.from(fileList || []);
    if (files.length === 0 || !projectId) { return; }
    setUpload({ added: [], paths: [], busy: true, error: null });
    const taken = [...(existing || inputFiles.map((entry) => entry.name))];
    const added = [];
    const paths = [];
    try {
      for (const file of files) {
        const name = uniqueName(file.name, taken);
        taken.push(name);
        // eslint-disable-next-line no-await-in-loop -- one at a time keeps the order and the names unique
        await vfsClient.writeBinaryFile(projectId, `${dir}/${name}`, file);
        added.push(name);
        paths.push(`${dir}/${name}`);
      }
      setUpload({ added, paths, busy: false, error: null });
    } catch (error) {
      setUpload({ added, paths, busy: false, error: error.message || 'The upload failed' });
    }
    refreshBrain();
  };
  const askAssistantToRead = () => {
    const list = (upload.paths || upload.added.map((name) => `${INPUTS_DIR}/${name}`)).join(', ');
    const one = upload.added.length === 1;
    window.dispatchEvent(new CustomEvent('askAIFromLogs', {
      detail: { message: `I added ${list}. Read ${one ? 'it' : 'them'} and tell me what ${one ? 'it contains' : 'they contain'}.` },
    }));
  };

  const services = servicesWithFolders(configuration?.services, sandbox ? serviceFolders : null);
  const brandNew = isBrandNew({ loaded: brainLoaded, services: services.length, inputs: inputFiles.length, outputs: outputFiles.length });
  // Added or deleted here: every reader of the services follows (the store, then Canvas's configuration).
  const imported = useCallback(() => {
    if (sandbox) { notifyServicesChanged(projectId); } else { onServicesChanged?.(); }
    refreshBrain();
  }, [sandbox, projectId, onServicesChanged, refreshBrain]);
  // Adding a service (Start by… or Apps' "+"): never over one the project has.
  const takenNames = services.map(([key]) => shortServiceName(servicePath(key)));
  // The ways to add one (an empty service, a .zip, a folder, a git repository) are home/AddService.jsx's, shared with
  // the service dropdown's "+ Create new service".
  const projectImport = useAddService(projectId, { taken: takenNames, onDone: imported });
  const addService = projectImport.add;

  const dragDepth = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const dropProps = projectId && canEdit ? {
    onDragEnter: (event) => {
      if (!isOsFileDrag(event.dataTransfer)) { return; }
      event.preventDefault();
      dragDepth.current += 1;
      setDragOver(true);
    },
    onDragOver: (event) => { if (isOsFileDrag(event.dataTransfer)) { event.preventDefault(); } },
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) { setDragOver(false); }
    },
    onDrop: (event) => {
      if (!isOsFileDrag(event.dataTransfer)) { return; }
      event.preventDefault();
      dragDepth.current = 0;
      setDragOver(false);
      // A drop on a brain folder of the Files card is saved there by the card itself.
      if (event.target?.closest?.('[data-brain-drop]')) { return; }
      // A brand-new project takes a folder or a zip as its code; anything else is an input for the assistant.
      if (brandNew && isProjectSourceDrop(event.dataTransfer)) {
        collectFromDataTransfer(event.dataTransfer).then(projectImport.importFrom).catch(() => {});
        return;
      }
      uploadFiles(event.dataTransfer?.files);
    },
  } : {};

  // ---- run and open ------------------------------------------------------------------------------------------------
  const runner = (key) => (sandbox ? sandboxServices.find((item) => item.serviceName === servicePath(key)) || null : null);
  const items = services.map(([key]) => ({ serviceName: servicePath(key), service: runner(key) }));
  // What Open app and the Preview gadget may open: the services, and the default app when it is a pinned file.
  // Start all / Stop all, Open app and the Preview gadget: the services with their pinned files' sessions (a service
  // its running compose file carries is not started nor opened on its own; projectRun projectRunItems).
  const runAllItems = sandbox ? projectRunItems(items, sandboxServices, defaultRunnable) : items;
  const appItems = runAllItems;
  const technologies = Array.isArray(vfsMetadata?.technologies) && vfsMetadata.technologies.length > 0
    ? vfsMetadata.technologies
    : [...new Set(services.map(([, s]) => s.dockerImage?.split(':')[0]?.split('/').pop()).filter(Boolean))];
  const openService = (key, service) => {
    markHomeStep('previewOpened');
    onResourceSelect?.({ type: 'service', name: key, data: service });
  };
  const [starting, setStarting] = useState(false);
  const runningApp = sandbox ? appItem(projectId, appItems, defaultApp) : null;
  const openAppButton = (() => {
    if (services.length === 0) { return null; }
    if (!sandbox) {
      const main = services.map(([key, service]) => ({ key, service })).find(({ key }) => String(key).replace(/^services\//, '') === defaultApp) || primaryService(services, technologies);
      return <button className="ph-btn ph-btn-primary" onClick={() => openService(main.key, main.service)} type="button">Open app</button>;
    }
    if (runningApp) {
      return <button className="ph-btn ph-btn-primary" onClick={() => openApp(projectId, runningApp)} title="Open the running app in a new tab" type="button">Open app<ArrowSquareOut aria-hidden size={14} /></button>;
    }
    if (!canRun) { return null; }
    const start = async() => {
      setStarting(true);
      try {
        await startAndOpenApp(projectId, primaryItem(appItems, defaultApp), { onChanged: refreshSandbox });
      } catch (error) {
        message.error(error.message || 'The app could not start');
      } finally {
        setStarting(false);
      }
    };
    return <button className="ph-btn" disabled={starting} onClick={start} title="Starts the app, then opens it" type="button">{starting ? 'Starting…' : 'Open app'}{!starting && <ArrowSquareOut aria-hidden size={14} />}</button>;
  })();
  // Open app and Start/Stop all: in the strip (Open app first), or, where the header's tab row has room for them
  // (`toolsSlot`), at its right end (Start all, then Open app).
  const runAll = sandbox && canRun && services.length > 0
    ? <RunAllActions hideDisabled onChanged={refreshSandbox} onOpenLogs={onOpenLogs} projectId={projectId} services={runAllItems} size="middle" />
    : null;
  // The workspace header has its own run control on every tab (sandbox/ProjectRunTools): not twice on Home.
  const hasRunTools = !brandNew && !runToolsInHeader && Boolean(runAll || openAppButton);
  const continueInChat = chatOn && onContinueInChat ? () => {
    window.dispatchEvent(new CustomEvent('flow:compactPane', { detail: { pane: 'chat' } }));
    onContinueInChat(sessions[0]?.id || vfsMetadata?.conversationId || null);
    focusChat();
  } : null;
  // Apps' "No apps yet": the chat, focused, to describe the app.
  const askForApp = () => {
    window.dispatchEvent(new CustomEvent('flow:compactPane', { detail: { pane: 'chat' } }));
    focusChat();
  };
  const startInChat = (text) => {
    window.dispatchEvent(new CustomEvent('flow:compactPane', { detail: { pane: 'chat' } }));
    window.dispatchEvent(new CustomEvent('askAIFromLogs', { detail: { message: text } }));
  };

  const moreRef = useRef(null);
  // A tab's link, or null where the plan hides that tab.
  const tabLink = (tab) => (planShows(tab) ? () => openHistory({ tab }) : null);
  const vfsProject = Boolean(projectId && sandbox);
  const extraGadgets = {
    // Build
    preview: vfsProject ? (
      <PreviewGadget canRun={canRun} items={appItems} onChanged={refreshSandbox} onOpenLogs={planShows('logs') ? onOpenLogs : null} projectId={projectId} runAll={runAll} />
    ) : null,
    code: vfsProject && collab ? <CodeActivity onOpenChanges={tabLink('changes')} onOpenGit={tabLink('git')} projectId={projectId} /> : null,
    deps: vfsProject ? <DependenciesGadget onOpenQuality={tabLink('quality')} projectId={projectId} /> : null,
    agents: collab ? <AgentsAtWork canEdit={canEdit} onOpenMessages={() => openMessages({ projectId, view: 'list' })} presence={presence} projectId={projectId} /> : null,
    // A gadget with settings: HomeGadgets passes the chosen file (per project) and saves a new choice with the layout.
    // Open (and a Markdown link in it) opens a FILE: a service's in Code on that service, any other in the Files tab.
    // Not onOpenFiles: that roots the Files view's tree at a folder, and a file there was listed as one ("Not a directory").
    file: projectId ? ({ settings, onSettings }) => (
      <FileGadget onOpenFile={onOpenFiles ? (path) => openProjectFile(path, { filesTab: planShows('files') }) : null} onSettings={onSettings} projectId={projectId} settings={settings} />
    ) : null,
    todo: vfsProject ? <OpenWork onOpenConfig={tabLink('config')} onOpenQuality={tabLink('quality')} projectId={projectId} /> : null,
    // Verify
    tests: vfsProject ? <TestsGadget onOpenQuality={tabLink('quality')} projectId={projectId} /> : null,
    errors: vfsProject ? (
      <ErrorsGadget
        items={items}
        onAskAgent={chatOn ? startInChat : null}
        onOpenLogs={planShows('logs') ? onOpenLogs : null}
        onOpenTrace={planShows('traces') ? openTraceInTab : null}
        onOpenTraces={tabLink('traces')}
        projectId={projectId}
      />
    ) : null,
    apihealth: vfsProject ? <ApiHealthGadget onOpenApi={tabLink('api')} onOpenTraces={tabLink('traces')} projectId={projectId} /> : null,
    security: vfsProject ? <SecurityGadget onOpenQuality={tabLink('quality')} projectId={projectId} /> : null,
    // Ship
    release: vfsProject && collab ? <ReleaseGadget canEdit={canEdit} onOpenVersions={planShows('changes') ? () => openHistory({ tab: 'versions' }) : null} projectId={projectId} /> : null,
    envs: vfsProject && collab ? <EnvironmentsGadget onOpenActivity={tabLink('activity')} projectId={projectId} /> : null,
    confighealth: vfsProject ? <ConfigHealthGadget onOpenConfig={tabLink('config')} onOpenJobs={tabLink('jobs')} projectId={projectId} /> : null,
    // Operate
    uptime: vfsProject ? <UptimeGadget items={items} onOpenApps={tabLink('apps')} onOpenService={planShows('apps') ? (service) => openHistory({ tab: 'apps', focus: { service } }) : null} /> : null,
    jobs: vfsProject ? <JobsGadget canEdit={canEdit} onOpenJobs={tabLink('jobs')} projectId={projectId} /> : null,
    data: vfsProject ? <DataGadget onOpenData={tabLink('data')} projectId={projectId} /> : null,
    cost: vfsProject ? <CostGadget onOpenFinOps={tabLink('finops')} projectId={projectId} /> : null,
    backups: vfsProject ? <BackupsGadget canEdit={canEdit} onOpenData={tabLink('data')} projectId={projectId} /> : null,
    // Team
    people: collab ? <PeopleGadget onOpenMessages={openMessages} presence={presence} projectId={projectId} /> : null,
    reviews: collab ? <ReviewsGadget onOpenHandoff={onOpenHandoff} onOpenSuggestion={onOpenSuggestion} projectId={projectId} /> : null,
  };
  const map = buildMap(services, technologies);
  const endpoints = Object.entries(configuration?.endpoints || {}).slice(0, 6).map(([key, endpoint]) => endpoint.basePath || key);

  const uploadNote = upload.busy ? <div className="ph-muted ph-small" role="status">Uploading…</div>
    : upload.error ? <div className="ph-error" role="alert">{upload.error}</div>
      : upload.added.length > 0 ? (
        <div className="ph-added" role="status">
          <span>{upload.added.length === 1 ? `${upload.added[0]} added.` : `${upload.added.length} files added.`}</span>
          <button className="ph-link ph-link-inline" onClick={askAssistantToRead} type="button">Ask the assistant to read {upload.added.length === 1 ? 'it' : 'them'}</button>
        </div>
      ) : null;

  return (
    <div className="ph pd" data-testid="project-home" {...dropProps}>
      <div className="ph-scroll">
        <div className="ph-main is-full-width" data-testid="project-home-main">
          <HomeStrip
            lean
            canDelete={!yourRole || yourRole === 'owner'}
            canEdit={canEdit}
            deleting={deleting}
            description={displayDescription}
            name={displayName}
            onDelete={() => setConfirmDelete(true)}
            onRemix={sandbox && collab ? () => setRemixOpen(true) : null}
            onSave={saveField}
            presence={collab ? presence : null}
            {...favorite.stripProps}
            {...archive.stripProps}
            workspaces={sandbox && collab ? (
              <div className="ph-workspaces"><ProjectWorkspaceChips manage={!yourRole || yourRole === 'owner'} projectId={projectId} projectName={displayName} /></div>
            ) : null}
            status={sandbox && services.length > 0 && !toolsSlot ? <ProjectRunStatus projectId={projectId} serviceCount={services.length} serviceKeys={services.map(([key]) => key)} /> : null}
          >
            <span className="hg-slot" ref={setBarSlot} />
            {hasRunTools && !toolsSlot && <>{openAppButton}{runAll}</>}
            {!brandNew && continueInChat && <button className="ph-btn" onClick={continueInChat} type="button">Continue in chat</button>}
            {projectId && (
              <button aria-label="Refresh Home" className="ph-btn" data-testid="home-refresh" disabled={refreshing} onClick={refreshNow} title="Refresh the services, what changed and the files (it also refreshes by itself)" type="button">
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
          </HomeStrip>
          {archive.banner}
          {projectId && <ConsoleDeliveryStatus projectId={projectId} />}
          {hasRunTools && toolsSlot && createPortal(<div className="pd workspace-home-tools" data-testid="home-run-tools">{runAll}{openAppButton}</div>, toolsSlot)}

          {brandNew ? (
            canEdit && (
              <StartBy
                chatOn={chatOn}
                importBusy={projectImport.busy}
                importState={projectImport.state}
                onImport={projectImport.importFrom}
                onStartFromGit={projectImport.openGit}
                onStartInChat={startInChat}
              />
            )
          ) : (
            <>
              <HomeGadgets
                barSlot={barSlot}
                gadgets={{
                  // The links only into tabs the plan shows; "Ask the agent" only where chat is on.
                  attention: sandbox ? (
                    <NeedsAttention
                      items={items}
                      onAskAgent={chatOn ? startInChat : null}
                      onOpenLogs={planShows('logs') ? onOpenLogs : null}
                      onOpenQuality={planShows('quality') ? () => openHistory({ tab: 'quality' }) : null}
                      projectId={projectId}
                    />
                  ) : null,
                  apps: (
                    <WhereThings
                      addState={projectImport.state}
                      canEdit={canEdit}
                      canRun={canRun}
                      onAddService={projectId ? addService : null}
                      onAskAssistant={chatOn ? askForApp : null}
                      runAll={runAll}
                      onServiceDeleted={imported}
                      onChanged={refreshSandbox}
                      onOpenFiles={(path) => onOpenFiles?.(path)}
                      onOpenLogs={onOpenLogs}
                      onOpenApp={planShows('apps') ? (service) => openHistory({ tab: 'apps', focus: { service } }) : null}
                      onOpenCode={onOpenCode}
                      onOpenService={openService}
                      projectId={projectId || ''}
                      runner={runner}
                      sandbox={sandbox}
                      services={services}
                      tenantId={tenant?.tenantId || tenant?.id || null}
                    />
                  ),
                  changed: collab ? (
                    <WhatChanged
                      refreshKey={refreshKey}
                      onOpenAi={showAi ? () => moreRef.current?.showAi() : null}
                      onOpenHandoff={onOpenHandoff}
                      onOpenSuggestion={onOpenSuggestion}
                      projectId={projectId}
                      shadow={ai.shadow}
                      you={presence?.you || null}
                    />
                  ) : null,
                  files: projectId ? (
                    <FilesSection
                      canEdit={canEdit}
                      dragging={dragOver}
                      inputs={inputFiles}
                      inputsDir={INPUTS_DIR}
                      onAddFiles={() => fileInputRef.current?.click()}
                      onOpenAll={planShows('files') ? () => openHistory({ tab: 'files' }) : null}
                      onOpenFiles={(path) => onOpenFiles?.(path)}
                      outputs={outputFiles}
                      outputsDir={OUTPUTS_DIR}
                      projectId={projectId}
                      upload={uploadNote}
                    />
                  ) : null,
                  brain: projectId ? (
                    <BrainGadget
                      canEdit={canEdit}
                      folders={brainFolders}
                      onOpenFiles={(path) => onOpenFiles?.(path)}
                      onUploadToBrain={(dir, picked, taken) => uploadFiles(picked, dir, taken)}
                      projectId={projectId}
                    />
                  ) : null,
                  ready: projectId && sandbox ? <ReadyToPublish onOpenQuality={planShows('quality') ? () => openHistory({ tab: 'quality' }) : null} projectId={projectId} /> : null,
                  requests: projectId && sandbox ? <RequestsGadget onOpenTraces={planShows('traces') ? () => openHistory({ tab: 'traces' }) : null} projectId={projectId} /> : null,
                  plan: sandbox ? <PlanUsage /> : null,
                  // The gadgets hidden by default (+ Add gadget). Each reads its data only once it is on the board.
                  ...extraGadgets,
                  more: projectId ? (
                    <MoreAboutProject
                      canEdit={canEdit}
                      conversations={sessions}
                      currentUserSub={user?.sub}
                      endpoints={endpoints}
                      map={map}
                      onOpenService={openService}
                      personId={userId}
                      projectId={projectId}
                      ref={moreRef}
                      refreshKey={refreshKey}
                      restorePoints={collab && sandbox}
                      showAi={showAi}
                      technologies={technologies}
                    />
                  ) : null,
                }}
              />
            </>
          )}
          <input data-testid="home-file-input" hidden multiple onChange={(event) => { uploadFiles(event.target.files); event.target.value = ''; }} ref={fileInputRef} type="file" />
        </div>
      </div>
      {dragOver && (
        <div aria-hidden="true" className="ph-drop-hint">
          {brandNew ? 'Drop a folder or a .zip to import it, or files to add them to Inputs' : 'Drop files to add them to Inputs'}
        </div>
      )}
      {projectId && canEdit && projectImport.dialogs}
      {archive.dialog}
      {remixOpen && <NewProjectDialog onClose={() => setRemixOpen(false)} open remixOf={projectId} />}
      <ConfirmDialog
        isOpen={confirmDelete}
        message={`Are you sure you want to delete "${displayName || 'this project'}"? This action cannot be undone.`}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={deleteProject}
        title="Delete project"
      />
    </div>
  );
};

DashboardView.propTypes = {
  configuration: PropTypes.shape({
    endpoints: PropTypes.object,
    services: PropTypes.object,
  }),
  onContinueInChat: PropTypes.func,
  onOpenCode: PropTypes.func,
  toolsSlot: PropTypes.object,
  runToolsInHeader: PropTypes.bool,
  onOpenFiles: PropTypes.func,
  onOpenHandoff: PropTypes.func,
  onOpenLogs: PropTypes.func,
  onOpenSuggestion: PropTypes.func,
  onProjectDeleted: PropTypes.func,
  onProjectUpdated: PropTypes.func,
  onResourceSelect: PropTypes.func,
  onServicesChanged: PropTypes.func,
  onVfsMetadataUpdated: PropTypes.func,
  selectedProject: PropTypes.shape({
    description: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  tenant: PropTypes.shape({
    id: PropTypes.string,
    tenantId: PropTypes.string,
  }),
  vfsMetadata: PropTypes.shape({
    conversationId: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    technologies: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default DashboardView;
